import React from "react";
import { Link } from "react-router-dom";
import Logo from "../Logo";
import useSEO from "../../lib/useSEO";

const SECTIONS = [
  { h: "1. Acceptance", p: [
    "By accessing or using the Capittance platform (including the Harmonizer product, Executive Portal, and any related APIs — the \u201cService\u201d), you agree to these Terms of Service. If you do not agree, do not use the Service.",
    "You must be at least 18 years old and legally able to enter into a binding contract in your jurisdiction. If you are accepting on behalf of an organisation, you represent that you have the authority to bind that organisation.",
  ]},
  { h: "2. The Service", p: [
    "Capittance provides a subscription-based project delivery operating system with two tiers: Basic Portal (\u201cStarter\u201d) at $200 per month and Executive Portal (\u201cPlatinum\u201d) at $350 per month. Prices are per user, billed monthly in USD via Stripe.",
    "Feature availability by tier is described on the /signup page and may change from time to time. We will provide reasonable notice before removing material features from an active plan.",
  ]},
  { h: "3. Payments, renewals and cancellation", p: [
    "Subscriptions renew automatically each month on your billing date until cancelled. You may cancel at any time from your account settings or by emailing Requests@Capittance.com.",
    "All payments are non-refundable. Cancellation stops the next billing cycle; access to paid features ends immediately upon cancellation with no proration. Downgrades take effect at the next billing date.",
    "Payment processing is handled by Stripe under Stripe\u2019s own terms; Capittance never stores full card numbers.",
  ]},
  { h: "4. Your data & ownership", p: [
    "You retain all rights, title and interest in the data, documents, project artefacts, connector credentials, and other content you submit to the Service (\u201cCustomer Data\u201d).",
    "You grant Capittance a limited, non-exclusive licence to host, process, transmit and display Customer Data solely to provide the Service to you.",
    "You are responsible for the accuracy, legality, and appropriateness of Customer Data, and for obtaining any consents required to process it (for example, from your users, employees, or subjects).",
    "Where Capittance acts as a data processor on your behalf under GDPR, UK-GDPR, or CCPA, we process personal data only to provide the Service. Details of our processing, sub-processors, and data-subject rights are set out in our Privacy Policy.",
  ]},
  { h: "5. AI features (NAVBOT)", p: [
    "NAVBOT (our C-Suite AI assistant) uses Anthropic\u2019s Claude Sonnet 4.5 model to generate summaries and drafts based on your prompts and portfolio data. Generated text may be incorrect, incomplete, or biased \u2014 review before relying on it for material decisions.",
    "Prompts and responses are processed by our LLM provider under a zero-retention posture. See the Privacy Policy for details.",
  ]},
  { h: "6. Third-party integrations", p: [
    "The Service can connect to third-party systems (e.g., Microsoft Dynamics 365, SAP S/4HANA, Stripe, Anthropic, Resend). Use of these integrations is governed by their own terms. You are responsible for the credentials you provide and for maintaining appropriate access controls on your side.",
  ]},
  { h: "7. Acceptable use", p: [
    "You will not: (a) reverse engineer, decompile, or attempt to derive the source code except to the extent expressly permitted by applicable law; (b) resell or sublicense the Service without our written consent; (c) upload malicious code or content that infringes rights of others; (d) use the Service to build a competing product; (e) attempt to interfere with the integrity or security of the Service.",
  ]},
  { h: "8. Confidentiality", p: [
    "Each party will protect the other party\u2019s confidential information using at least the same degree of care it uses to protect its own confidential information of like importance, and not less than a reasonable standard of care.",
  ]},
  { h: "9. Availability & support", p: [
    "The Service is provided on a commercially reasonable basis. Planned maintenance windows will be announced when practical. We do not warrant that the Service will be uninterrupted or error-free.",
    "Support requests are handled by email at Requests@Capittance.com during standard business hours.",
  ]},
  { h: "10. Warranties & disclaimers", p: [
    "EXCEPT AS EXPLICITLY STATED HEREIN, THE SERVICE IS PROVIDED \u201cAS IS\u201d WITHOUT WARRANTIES OF ANY KIND, EXPRESS OR IMPLIED, INCLUDING WITHOUT LIMITATION WARRANTIES OF MERCHANTABILITY, FITNESS FOR A PARTICULAR PURPOSE, AND NON-INFRINGEMENT.",
  ]},
  { h: "11. Limitation of liability", p: [
    "TO THE MAXIMUM EXTENT PERMITTED BY LAW, IN NO EVENT SHALL CAPITTANCE BE LIABLE FOR ANY INDIRECT, INCIDENTAL, CONSEQUENTIAL, SPECIAL OR PUNITIVE DAMAGES. AGGREGATE LIABILITY UNDER THESE TERMS SHALL NOT EXCEED THE FEES YOU PAID TO CAPITTANCE IN THE 12 MONTHS PRECEDING THE CLAIM.",
  ]},
  { h: "12. Termination", p: [
    "You may terminate at any time by cancelling your subscription. We may suspend or terminate your access for material breach, non-payment, or violation of the acceptable-use clause after reasonable notice where practicable. Sections 4, 8, 10, 11 and 13 survive termination.",
  ]},
  { h: "13. Governing law and dispute resolution", p: [
    "These Terms are governed by the laws of the jurisdiction in which Capittance is registered, excluding its conflict-of-laws rules. Any dispute arising out of or relating to these Terms shall first be attempted to be resolved in good faith by written notice for 30 days; failing that, the dispute shall be resolved by binding arbitration on an individual basis.",
    "You waive any right to participate in a class action against Capittance to the extent permitted by law.",
  ]},
  { h: "14. Changes to these Terms", p: [
    "We may update these Terms from time to time. Material changes will be announced by email or in-app notice at least 30 days before they take effect. Continued use after the effective date constitutes acceptance.",
  ]},
  { h: "15. Contact", p: [
    "All notices, requests, and legal enquiries should be sent to Requests@Capittance.com.",
  ]},
];

/**
 * SaaS-standard Terms of Service. This is a starting template — send to your
 * legal counsel before treating it as a final, binding contract.
 */
export default function Terms() {
  useSEO({
    title: "Terms of Service · Capittance Harmonizer subscription agreement",
    description: "The plain-English contract for using Capittance Harmonizer — Starter $200/mo and Platinum $350/mo. Non-refundable, cancel any time, no proration. Effective 18 February 2026.",
    path: "/terms",
  });
  return (
    <div className="min-h-screen bg-white">
      <LegalHeader title="Terms of Service" subtitle="Effective 18 February 2026" />
      <main className="max-w-3xl mx-auto px-6 py-10" data-testid="page-terms">
        <p className="text-sm text-zinc-500 mb-8 leading-relaxed">
          This is a plain-English contract between you and Capittance. It replaces any prior agreement between us covering the same subject matter. Read it carefully — using our Service means you accept it.
        </p>
        {SECTIONS.map((s, i) => (
          <section key={i} className="mb-8">
            <h2 className="font-display text-lg font-extrabold tracking-tight text-zinc-900 mb-2">{s.h}</h2>
            {s.p.map((para, j) => <p key={j} className="text-sm text-zinc-700 leading-relaxed mb-2">{para}</p>)}
          </section>
        ))}
        <LegalFooter />
      </main>
    </div>
  );
}

export function LegalHeader({ title, subtitle }) {
  return (
    <div className="border-b border-[var(--border)] bg-white sticky top-0 z-10">
      <div className="max-w-3xl mx-auto px-6 py-3 flex items-center justify-between gap-4">
        <Link to="/" className="inline-flex items-center gap-2" aria-label="Capittance home" data-testid="legal-home">
          <Logo height={78} />
        </Link>
        <div className="text-right">
          <div className="font-display text-lg font-extrabold tracking-tight">{title}</div>
          <div className="font-mono text-[10px] uppercase tracking-widest text-zinc-500">{subtitle}</div>
        </div>
      </div>
    </div>
  );
}

export function LegalFooter() {
  return (
    <div className="mt-12 pt-6 border-t border-[var(--border)] flex items-center justify-between font-mono text-[10px] uppercase tracking-widest text-zinc-500 flex-wrap gap-2">
      <div>
        <Link to="/about" className="hover:text-zinc-900">About</Link>
        {" · "}
        <Link to="/pricing" className="hover:text-zinc-900">Pricing</Link>
        {" · "}
        <Link to="/resources" className="hover:text-zinc-900">Resources</Link>
        {" · "}
        <Link to="/guides" className="hover:text-zinc-900" data-testid="footer-guides">Guides</Link>
        {" · "}
        <Link to="/compare" className="hover:text-zinc-900" data-testid="footer-compare">Compare</Link>
        {" · "}
        <Link to="/tools" className="hover:text-zinc-900" data-testid="footer-tools">Free tools</Link>
        {" · "}
        <Link to="/terms" className="hover:text-zinc-900">Terms</Link>
        {" · "}
        <Link to="/privacy" className="hover:text-zinc-900">Privacy</Link>
        {" · "}
        <Link to="/contact" className="hover:text-zinc-900">Contact</Link>
      </div>
      <div>
        Support · <a href="mailto:Requests@Capittance.com?subject=Legal%20enquiry" className="underline hover:no-underline">Requests@Capittance.com</a>
      </div>
    </div>
  );
}
